import { StyledTileIndicator } from "@/components/TilesModule/variants/WhoHelpsTilesModule"
import Text from "@/components/common/Text"
import Image, { formatImageCMSData } from "@/components/common/Image"
import { getCssSizeValue, isStringEmpty } from "@/utils/utils"
import styled from "styled-components"
import { BREAKPOINTS } from "@/styles/themeSettings"
import { getImageSource } from "@/components/TeamModule/TeamMember"
import useGlobals from "@/hooks/useGlobals"

const StyledWrapper = styled.div`
	--aspect-ratio: 540/356;
	--cols-count: 5;
	aspect-ratio: var(--aspect-ratio);
	min-height: ${getCssSizeValue(356)};

	display: grid;
	grid-template-columns: repeat(var(--cols-count), 1fr);
	column-gap: var(--grid-gap);
	position: relative;

	border: 1px solid ${(props) => props.theme.colors.richBlack};
	margin-bottom: ${getCssSizeValue(48, BREAKPOINTS.MOBILE)};

	@media (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
		margin-bottom: ${getCssSizeValue(48, BREAKPOINTS.TABLET)};
	}

	@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
		margin-bottom: unset;
	}
`

const ImageWrapper = styled.div`
	grid-column: span 2;
	overflow: hidden;

	&.default {
		display: flex;
		align-items: center;
		justify-content: center;
		background: ${({ theme }) => theme.colors.whiteSmoke};
	}
`

const StyledImage = styled(Image)`
	aspect-ratio: 9/16;
	width: auto;
	height: 100%;

	&.default {
		height: ${getCssSizeValue(90, BREAKPOINTS.MOBILE)};
		aspect-ratio: 1;

		@media (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
		}

		@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
			height: ${getCssSizeValue(150)};
		}
	}
`

const LogoAndMembersDataWrapper = styled.div`
	grid-column: span 3;
	display: flex;
	flex-direction: column;
`

const StyledIcon = styled(Image)`
	aspect-ratio: ${({ $aspectRatio }) => $aspectRatio || "16/9"};
	width: auto;
	height: ${getCssSizeValue(70, BREAKPOINTS.MOBILE)};
	margin: ${getCssSizeValue(16, BREAKPOINTS.MOBILE)} 0 ${getCssSizeValue(20, BREAKPOINTS.MOBILE)}
		${getCssSizeValue(20, BREAKPOINTS.MOBILE)};

	@media (min-width: ${({ theme }) => theme.breakpoints.tablet || 768}px) {
		height: ${getCssSizeValue(100, BREAKPOINTS.TABLET)};
		margin-top: ${getCssSizeValue(16, BREAKPOINTS.TABLET)};
	}

	@media (min-width: ${({ theme }) => theme.breakpoints.desktop || 1024}px) and (orientation: landscape) {
		height: ${getCssSizeValue(100)};
		margin-top: ${getCssSizeValue(32)};
		margin-bottom: ${getCssSizeValue(40)};
		margin-left: ${getCssSizeValue(20)};
	}
`

const MembersData = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1;
	row-gap: ${getCssSizeValue(10, BREAKPOINTS.MOBILE)};
	margin-left: ${getCssSizeValue(22, BREAKPOINTS.MOBILE)};

	@media (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
		margin-left: ${getCssSizeValue(24, BREAKPOINTS.TABLET)};
	}

	@media (min-width: ${({ theme }) => theme.breakpoints.desktop || 1024}px) and (orientation: landscape) {
		row-gap: ${getCssSizeValue(28)};
		padding-bottom: ${getCssSizeValue(24)};
		justify-content: space-between;
	}
`

const DataRow = styled.div`
	display: flex;
	flex-direction: column;

	@media (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
	}

	@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
		gap: ${getCssSizeValue(4)};
	}
`

function getData(data) {
	return {
		nameAndSurname: data?.member?.nameAndSurname || "",
		phoneNumber: data?.member?.phoneNumber || "",
		email: data?.member?.email || "",
		role: data?.member?.role || "",
		gender: data?.member?.gender || "male",
		sectionHeader: data?.sectionHeader || "",
		image: formatImageCMSData(data?.member?.verticalImage || {}),
		icon: formatImageCMSData(data?.member?.icon || {}),
	}
}

export default function Tile({ tile }) {
	const maleDefaultImage = useGlobals((state) => state.teamMemberMaleDefaultImage)
	const femaleDefaultImage = useGlobals((state) => state.teamMemberFemaleDefaultImage)
	const { nameAndSurname, phoneNumber, image: memberImage, email, icon, role, sectionHeader, gender } = getData(tile)
	const { image, defaultImage } = getImageSource(gender, memberImage, maleDefaultImage, femaleDefaultImage)

	return (
		<StyledWrapper className="col-desktop-5 col-mobile-8">
			{!isStringEmpty(sectionHeader) && (
				<StyledTileIndicator className="typo-strawford typo-strawford-113">
					<Text className="square static">{sectionHeader}</Text>
				</StyledTileIndicator>
			)}
			<ImageWrapper className={defaultImage ? "default" : ""}>
				<StyledImage src={image.url} alt={image.alt} focalPoint={image.focalPoint} className={defaultImage ? "default" : ""} />
			</ImageWrapper>
			<LogoAndMembersDataWrapper>
				<div>
					<StyledIcon src={icon?.url || ""} alt={icon?.alt || ""} focalPoint={icon.focalPoint} $aspectRatio={icon.aspectRatio} />
				</div>
				<MembersData>
					<DataRow>
						<Text className="typo-strawford typo-strawford-60">{nameAndSurname}</Text>
						<Text className="typo-strawford typo-strawford-14">{role}</Text>
					</DataRow>
					<DataRow>
						<a href={`tel:${phoneNumber}`} className="typo-strawford typo-strawford-60">
							{phoneNumber}
						</a>
						<a href={`mailto:${email}`} className="typo-strawford typo-strawford-14">
							{email}
						</a>
					</DataRow>
				</MembersData>
			</LogoAndMembersDataWrapper>
		</StyledWrapper>
	)
}
